import { store } from "quasar/wrappers";
import { createStore } from "vuex";
import createPersistedState from "vuex-persistedstate";
import Cookies from "js-cookie";

// Safe localStorage wrapper for Node.js 25.2+ compatibility
// Returns null if localStorage is not available (e.g., during build)
function getSafeLocalStorage() {
  try {
    if (typeof localStorage !== "undefined") {
      // Test that it actually works
      const testKey = "__storage_test__";
      localStorage.setItem(testKey, testKey);
      localStorage.removeItem(testKey);
      return localStorage;
    }
  } catch (e) {
    // localStorage not available
  }
  return null;
}

/*
 * If not building with SSR mode, you can
 * directly export the Store instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Store instance.
 */

//app flags
import app from "./app";

//app authentication
import auth from "./auth";

//app system data
import data from "./data";

//app notifications
import notifications from "./notifications";

//current filters
import current from "./current";

//instant data (no persisted state)
import instant from "./instant";

import cart from "./cart";

import inventory from "./inventory";

//add brand form data
import brand from "./brand";

//add product form data
import product from "./product";

//add listing form data
import listing from "./listing";

//add buyer request form data
import buyerRequest from "./buyer-request";

//listing V2
import listingV2 from "src/store/listingV2";

//filters applied via filter panel
import panelFilters from "./panel-filters";

//marketplace Brand facet envelope
import brandFacet from "./brand-facet";

// Marketplace Facet
import facet from "./facet";

//Shipment
import shipment from "src/store/shipment";

//Inventory Reorder
import inventoryReorder from "src/store/inventory-reorder";

//Transfers
import transfers from "src/store/transfers";

//Contacts
import contacts from "src/store/contacts";

//Metrc
import metrc from "src/store/metrc";

//Pricing Wizard
import pricingWizard from "./pricing-wizard";
//listings cache
import listingsCache from "./listingsCache";

//auctions cache
import auctionsCache from "./auctionsCache";

//excluded packages
import excludedPackages from "./excluded-packages";

//package management (unified)
import packageManagement from "./package-management";

export default store(function (/* { ssrContext } */) {
  const COOKIE_DOMAIN = process.env.COOKIE_STORE_DOMAIN;
  const COOKIE_NAME = process.env.COOKIE_STORE_NAME;
  const COOKIE_LIFETIME = process.env.COOKIE_LIFETIME; //in minutes.
  const IS_WEB_APP = process.env.APP_TYPE === "web";

  return createStore({
    modules: {
      auth,
      app,
      data,
      current,
      instant,
      listing,
      product,
      brand,
      notifications,
      buyerRequest,
      listingV2,
      panelFilters,
      brandFacet,
      shipment,
      inventoryReorder,
      cart,
      inventory,
      transfers,
      contacts,
      metrc,
      pricingWizard,
      listingsCache,
      auctionsCache,
      excludedPackages,
      packageManagement,
      facet,
    },
    plugins: [
      //User data will be saved in cookies.
      createPersistedState({
        key: `${COOKIE_NAME}-auth`,
        paths: ["auth"],
        storage: IS_WEB_APP
          ? {
              //use cookies for auth only in web app.
              getItem: (key) => Cookies.get(key, { domain: COOKIE_DOMAIN }),
              setItem: (key, value) => {
                let parsedValue = JSON.parse(value);
                if (typeof parsedValue === 'object' && typeof parsedValue['auth'] !== 'undefined') {
                  if (typeof parsedValue.auth['user'] === 'object' && typeof parsedValue.auth.user['permissions'] !== 'undefined') {
                    /*
                    //Delete big data to not exceed cookies limit.
                    delete parsedValue.auth.user['permissions'];
                    delete parsedValue.auth.user['restriction_groups'];
                    delete parsedValue.auth.user['company_role'];
                    */

                    //Store only small set of data in order to not exceed cookies limit.
                    parsedValue.auth.user = {
                      id: parsedValue.auth.user?.id,
                      email: parsedValue.auth.user?.email
                    };

                    value = JSON.stringify(parsedValue);
                  }
                }
                let cookieExpiresAt = new Date(
                  new Date().getTime() + COOKIE_LIFETIME * 60 * 1000
                ); //add in milliseconds.
                Cookies.set(key, value, {
                  expires: cookieExpiresAt,
                  secure: location.protocol === "https:",
                  domain: COOKIE_DOMAIN,
                });
              },
              removeItem: (key) => {
                Cookies.remove(key, { domain: COOKIE_DOMAIN });
              }
            }
          : null,
      }),
      //User notifications will be saved in localStorage.
      createPersistedState({
        key: `${COOKIE_NAME}-notifications`,
        paths: ["notifications"],
        storage: getSafeLocalStorage(),
      }),
      //Data for new listing will be saved in localStorage.
      createPersistedState({
        key: `${COOKIE_NAME}-listing`,
        paths: ["listing"],
        storage: getSafeLocalStorage(),
      }),
      //Data for new product will be saved in localStorage.
      createPersistedState({
        key: `${COOKIE_NAME}-product`,
        paths: ["product"],
        storage: getSafeLocalStorage(),
      }),
      //Data for new brand will be saved in localStorage.
      createPersistedState({
        key: `${COOKIE_NAME}-brand`,
        paths: ["brand"],
        storage: getSafeLocalStorage(),
      }),
      //Data for current app state will be saved in localStorage.
      createPersistedState({
        key: `${COOKIE_NAME}-current`,
        paths: ["current"],
        storage: getSafeLocalStorage(),
      }),
      //Data for new buyer request will be saved in localStorage.
      createPersistedState({
        key: `${COOKIE_NAME}-buyer-request`,
        paths: ["buyerRequest"],
        storage: getSafeLocalStorage(),
      }),
      //Data for listing V2.
      createPersistedState({
        key: `${COOKIE_NAME}-listing-v2`,
        paths: ["listingV2"],
        storage: getSafeLocalStorage(),
      }),
    ],

    // enable strict mode (adds overhead!)
    // for dev mode and --debug builds only
    strict: process.env.DEBUGGING,
  });
});
